// src/GamePage/BuildOverview/ComboSection/Context/GameAssetContext.tsx

import React, { createContext, useState, useEffect, ReactNode, useCallback } from 'react';
import DataService from '../DataService';
import { ItemDetails } from '../GameSimulator/GameContainer2/GameGrid/ChampionCard/SettingsPanel/ItemShopPanel2';

// All images are now served from the public folder
// No more imports needed - just use string paths

// Create comprehensive rune icons mapping using public paths
const runeIcons = {
  // Rune Trees
  precision: '/Images/runes/7201_precision.png',
  domination: '/Images/runes/7200_domination.png',
  sorcery: '/Images/runes/7202_sorcery.png',
  resolve: '/Images/runes/7204_resolve.png',
  inspiration: '/Images/runes/7203_inspiration.png',

  // Precision Runes
  triumph: '/Images/runes/precision/triumph.png',
  pressTheAttack: '/Images/runes/precision/presstheattack.png',
  presenceOfMind: '/Images/runes/precision/presenceofmind.png',
  lethalTempo: '/Images/runes/precision/lethaltempotemp.png',
  legendHaste: '/Images/runes/precision/legendhaste.png',
  legendBloodline: '/Images/runes/precision/legendbloodline.png',
  legendAlacrity: '/Images/runes/precision/legendalacrity.png',
  fleetFootwork: '/Images/runes/precision/fleetfootwork.png',
  cutDown: '/Images/runes/precision/cutdown.png',
  coupDeGrace: '/Images/runes/precision/coupdegrace.png',
  conqueror: '/Images/runes/precision/conqueror.png',
  absorbLife: '/Images/runes/precision/absorblife.png',
  lastStand: '/Images/runes/precision/laststand.png',

  // Domination Runes
  zombieWard: '/Images/runes/domination/zombieward.png',
  ultimateHunter: '/Images/runes/domination/ultimatehunter.png',
  treasureHunter: '/Images/runes/domination/treasurehunter.png',
  tasteOfBlood: '/Images/runes/domination/tasteOfBlood.png',
  suddenImpact: '/Images/runes/domination/suddenimpact.png',
  sixthSense: '/Images/runes/domination/sixthsense.png',
  relentlessHunter: '/Images/runes/domination/relentlesshunter.png',
  predator: '/Images/runes/domination/predator.png',
  ingeniousHunter: '/Images/runes/domination/ingenioushunter.png',
  hailOfBlades: '/Images/runes/domination/hailofblades.png',
  grislyMementos: '/Images/runes/domination/grislymementos.png',
  ghostPoro: '/Images/runes/domination/ghostporo.png',
  eyeballCollection: '/Images/runes/domination/eyeballcollection.png',
  electrocute: '/Images/runes/domination/electrocute.png',
  deepWard: '/Images/runes/domination/deepward.png',
  darkHarvest: '/Images/runes/domination/darkharvest.png',
  cheapShot: '/Images/runes/domination/cheapshot.png',

  // Sorcery Runes
  waterWalking: '/Images/runes/sorcery/waterwalking.png',
  transcendence: '/Images/runes/sorcery/transcendence.png',
  summonAery: '/Images/runes/sorcery/summonaery.png',
  scorch: '/Images/runes/sorcery/scorch.png',
  phaseRush: '/Images/runes/sorcery/phaserush.png',
  axiomArcanist: '/Images/runes/sorcery/axiom_arcanist.png',
  nimbusCloak: '/Images/runes/sorcery/nimbusCloack.png',
  manaflowBand: '/Images/runes/sorcery/manaflowband.png',
  gatheringStorm: '/Images/runes/sorcery/gatheringstorm.png',
  celerity: '/Images/runes/sorcery/celeritytemp.png',
  arcaneComet: '/Images/runes/sorcery/arcanecomet.png',
  absoluteFocus: '/Images/runes/sorcery/absolutefocus.png',

  // Resolve Runes
  unflinching: '/Images/runes/resolve/unflinching.png',
  aftershock: '/Images/runes/resolve/aftershock.png',
  secondWind: '/Images/runes/resolve/secondwind.png',
  revitalize: '/Images/runes/resolve/revitalize.png',
  overgrowth: '/Images/runes/resolve/overgrowth.png',
  shieldBash: '/Images/runes/resolve/shieldBash.png',
  guardian: '/Images/runes/resolve/guardian.png',
  graspOfTheUndying: '/Images/runes/resolve/graspoftheundying.png',
  fontOfLife: '/Images/runes/resolve/fontoflife.png',
  demolish: '/Images/runes/resolve/demolish.png',
  conditioning: '/Images/runes/resolve/conditioning.png',
  bonePlating: '/Images/runes/resolve/boneplating.png',

  // Inspiration Runes
  approachVelocity: '/Images/runes/inspiration/approachvelocity.png',
  unsealedSpellbook: '/Images/runes/inspiration/unsealedspellbook.png',
  timeWarpTonic: '/Images/runes/inspiration/timewarptonic.png',
  tripleTonic: '/Images/runes/inspiration/tripleTonic.png',
  magicalFootwear: '/Images/runes/inspiration/magicalfootwear.png',
  jackOfAllTrades: '/Images/runes/inspiration/jackofalltrades.png',
  hextechFlashtraption: '/Images/runes/inspiration/hextechflashtraption.png',
  glacialAugment: '/Images/runes/inspiration/glacialaugment.png',
  firstStrike: '/Images/runes/inspiration/firststrike.png',
  cosmicInsight: '/Images/runes/inspiration/cosmicinsight.png',
  cashback: '/Images/runes/inspiration/cashback.png',
  biscuitDelivery: '/Images/runes/inspiration/biscuitdelivery.png',
};

// Crowd Control Icons mapping
const crowdControlIcons = {
  // PascalCase mappings to match backend CrowdControlType enum
  Stun: '/Images/CrowdControlIcons/Stun_icon.webp',
  Slow: '/Images/CrowdControlIcons/Slow_icon.webp',
  Root: '/Images/CrowdControlIcons/Root_icon.webp',
  Silence: '/Images/CrowdControlIcons/Silence_icon.webp',
  KnockUp: '/Images/CrowdControlIcons/Kinematics_icon.webp', // Using existing Kinematics for KnockUp
  KnockBack: '/Images/CrowdControlIcons/Kinematics_icon.webp', // Using existing Kinematics for KnockBack
  Fear: '/Images/CrowdControlIcons/Flee_icon.webp', // Using existing Flee for Fear
  Charm: '/Images/CrowdControlIcons/Charm_icon.webp',
  Sleep: '/Images/CrowdControlIcons/Sleep_icon.webp',
  Suppression: '/Images/CrowdControlIcons/Suppression_icon.webp',
  Taunt: '/Images/CrowdControlIcons/Taunt_icon.webp',
  Disarm: '/Images/CrowdControlIcons/Disarm_icon.webp',
  Grounded: '/Images/CrowdControlIcons/Grounded_icon.webp',
  Polymorph: '/Images/CrowdControlIcons/Stun_icon.webp', // Using Stun for Polymorph (similar effect)
  Blind: '/Images/CrowdControlIcons/Blind_icon.webp',
  Cripple: '/Images/CrowdControlIcons/Cripple_icon.webp',
  Airborne: '/Images/CrowdControlIcons/Airborne_icon.webp',
  Nearsight: '/Images/CrowdControlIcons/Blind_icon.webp', // Using Blind for Nearsight (similar effect)
  Drowsy: '/Images/CrowdControlIcons/Drowsy_icon.webp',
  Suspended: '/Images/CrowdControlIcons/Stasis_icon.webp', // Using Stasis for Suspended
  KnockDown: '/Images/CrowdControlIcons/KnockDown_icon.webp',
  Stasis: '/Images/CrowdControlIcons/Stasis_icon.webp',
  Pull: '/Images/CrowdControlIcons/Kinematics_icon.webp', // Using Kinematics for Pull
  Brittle: '/Images/CrowdControlIcons/Cripple_icon.webp', // Using Cripple for Brittle (similar effect)
  DisplacementImmunity: '/Images/CrowdControlIcons/Slow_immune_icon.webp',
  Flee: '/Images/CrowdControlIcons/Flee_icon.webp',
  Berserk: '/Images/CrowdControlIcons/Stun_icon.webp', // Using Stun for Berserk (loss of control)
  Suspension: '/Images/CrowdControlIcons/Stasis_icon.webp', // Using Stasis for Suspension

  // Legacy lowercase mappings for backward compatibility
  airborne: '/Images/CrowdControlIcons/Airborne_icon.webp',
  blind: '/Images/CrowdControlIcons/Blind_icon.webp',
  charm: '/Images/CrowdControlIcons/Charm_icon.webp',
  cripple: '/Images/CrowdControlIcons/Cripple_icon.webp',
  disarm: '/Images/CrowdControlIcons/Disarm_icon.webp',
  drowsy: '/Images/CrowdControlIcons/Drowsy_icon.webp',
  fear: '/Images/CrowdControlIcons/Flee_icon.webp', // Using existing Flee for fear
  flee: '/Images/CrowdControlIcons/Flee_icon.webp',
  grounded: '/Images/CrowdControlIcons/Grounded_icon.webp',
  knockBack: '/Images/CrowdControlIcons/Kinematics_icon.webp', // Using existing Kinematics
  knockDown: '/Images/CrowdControlIcons/KnockDown_icon.webp',
  knockUp: '/Images/CrowdControlIcons/Airborne_icon.webp', // Using existing Kinematics
  nearsight: '/Images/CrowdControlIcons/Blind_icon.webp', // Using Blind for nearsight
  polymorph: '/Images/CrowdControlIcons/Stun_icon.webp', // Using Stun for polymorph
  root: '/Images/CrowdControlIcons/Root_icon.webp',
  silence: '/Images/CrowdControlIcons/Silence_icon.webp',
  sleep: '/Images/CrowdControlIcons/Sleep_icon.webp',
  slow: '/Images/CrowdControlIcons/Slow_icon.webp',
  stasis: '/Images/CrowdControlIcons/Stasis_icon.webp',
  stun: '/Images/CrowdControlIcons/Stun_icon.webp',
  suppression: '/Images/CrowdControlIcons/Suppression_icon.webp',
  suspended: '/Images/CrowdControlIcons/Stasis_icon.webp', // Using Stasis for suspended
  taunt: '/Images/CrowdControlIcons/Taunt_icon.webp',
};

// Role/Lane Icons mapping
const roleIcons: Record<string, string> = {
  TOP: '/Images/top_icon.png',
  JUNGLE: '/Images/jgl_icon.png',
  MIDDLE: '/Images/mid_icon.png',
  BOTTOM: '/Images/bot_icon.png',
  UTILITY: '/Images/sup_icon.png',
};

// Other game assets
const gameAssets = {
  autoAttack: '/Images/Basic_Attack.png',
  critPhysical: '/Images/Crit_Physical.webp',
  critMagic: '/Images/Crit_Magic.webp',
  critTrue: '/Images/Crit_True.webp',
  shield: '/Images/Shield_icon.webp',
  death: '/Images/death_icon.webp',
  healEmpowered: '/Images/Healing_empowered_icon.png',
  energy: '/Images/Energy_Icon.png',
  attackRange: '/Images/Attack_Range_Icon.png',
  abilityHaste: '/Images/Ability_Haste_Icon.png',
  kircheisShard: '/Images/Kircheis_Shard.png',
  rune: '/Images/Rune_IconWhite.png',
  champion: '/Images/championImageWhite.png',
  skillOrder: '/Images/SkillOrderWhite.png',
  item: '/Images/MoneyIcon_White.png',
  slowResist: '/Images/slowResistIcon.png',
  damageReduction: '/Images/DamageIcon.png',
  lolCalculator: '/Images/lolCalculatorLogoWithNameNoBackground.png',
  lolCalculator2: '/Images/logoV3.png',
  lolCalculator3: '/Images/logoSimpleButtons.png',
  trueDamage: '/Images/trueDamageIcon.png',
  damageIcon: '/Images/DamageIcon.png',
  damageIconWhite: '/Images/DamageIconWhite.png',
  turretIcon: '/Images/towerWhite.png',
  turretPlateIcon: '/Images/turret_plateEmptyWhite8.png',
  minionIcon: '/Images/Minion_icon.webp',
  summonersRiftMap: '/Images/riftmap.jpg',
};  

interface GameWideAssets {
  runes: Record<string, string>;
  runeTrees: Record<string, string>;
  runeIcons: Record<string, string>;
  shards: Record<string, string>;
  itemsDetails: ItemDetails[];
  itemImages: Record<string, string>;
  summonerSpellImages: Record<string, string>;
  defaultChampionImage: string;
  critIcons: {
    physical: string;
    magic: string;
    true: string;
  };
  ccIcons: Record<string, string>;
  roleIcons: Record<string, string>;
  shieldIcon: string;
  deathIcon: string;
  healEmpoweredIcon: string;
  energyIcon: string;
  attackRangeIcon: string;
  abilityHasteIcon: string;
  kircheisShardIcon: string;
  runeIcon: string;
  championIcon: string;
  skillOrderIcon: string;
  itemIcon: string;
  trueDamageIcon: string;
  damageIcon: string;
  damageIconWhite: string;
  damageReductionIcon: string;
  slowResistIcon: string;
  lolCalculatorIcon: string;
  lolCalculatorIcon2: string;
  lolCalculatorIcon3: string;
  turretIcon: string;
  turretPlateIcon: string;
  minionIcon: string;
  summonersRiftMap: string;
}

interface ChampionAssets {
  championImageUrl: string;
  spellImageUrls: Record<string, string>;
  passiveImageUrl: string;
  autoAttackImageUrl: string;
}

interface GameAssetsContextState {
  gameWideAssets: GameWideAssets;
  championsAssets: Record<string, ChampionAssets>;
  fetchAndSetChampionAssets: (championName: string) => void;
}

const defaultGameWideAssets: GameWideAssets = {
  runes: {},
  runeTrees: {},
  runeIcons,
  shards: {},
  itemsDetails: [],
  itemImages: {},
  summonerSpellImages: {},
  defaultChampionImage: 'https://raw.communitydragon.org/latest/plugins/rcp-be-lol-game-data/global/default/assets/characters/tft_trainingdummy/hud/icons2d/tft_trainingdummy_donothing.png',
  critIcons: {
    physical: gameAssets.critPhysical,
    magic: gameAssets.critMagic,
    true: gameAssets.critTrue,
  },
  ccIcons: crowdControlIcons,
  roleIcons,
  shieldIcon: gameAssets.shield,
  deathIcon: gameAssets.death,
  healEmpoweredIcon: gameAssets.healEmpowered,
  energyIcon: gameAssets.energy,
  attackRangeIcon: gameAssets.attackRange,
  abilityHasteIcon: gameAssets.abilityHaste,
  kircheisShardIcon: gameAssets.kircheisShard,
  runeIcon: gameAssets.rune,
  championIcon: gameAssets.champion,
  skillOrderIcon: gameAssets.skillOrder,
  itemIcon: gameAssets.item,
  damageReductionIcon: gameAssets.damageReduction,
  slowResistIcon: gameAssets.slowResist,
  lolCalculatorIcon: gameAssets.lolCalculator,
  lolCalculatorIcon2: gameAssets.lolCalculator2,
  lolCalculatorIcon3: gameAssets.lolCalculator3,
  trueDamageIcon: gameAssets.trueDamage,
  damageIcon: gameAssets.damageIcon,
  damageIconWhite: gameAssets.damageIconWhite,
  turretIcon: gameAssets.turretIcon,
  turretPlateIcon: gameAssets.turretPlateIcon,
  minionIcon: gameAssets.minionIcon,
  summonersRiftMap: gameAssets.summonersRiftMap,
  };

const defaultContextState: GameAssetsContextState = {
  gameWideAssets: defaultGameWideAssets,
  championsAssets: {},
  fetchAndSetChampionAssets: () => {},
};

const GameAssetsContext = createContext<GameAssetsContextState>(defaultContextState);

interface GameAssetsProviderProps {
  children: ReactNode;
  initialChampionName?: string;
}

export const GameAssetsProvider: React.FC<GameAssetsProviderProps> = ({ children, initialChampionName }) => {
  const [gameWideAssets, setGameWideAssets] = useState<GameWideAssets>(defaultGameWideAssets);
  const [championsAssets, setChampionsAssets] = useState<Record<string, ChampionAssets>>({});

  // Fetch and set game-wide assets only once or on specific condition
  useEffect(() => {
    async function fetchGameWideAssets() {
      const runeImagePaths = DataService.fetchAllRuneImagePaths();
      const shardImagePaths = DataService.fetchAllShardImagePaths();
      const runeTreeImagePaths = DataService.fetchAllRuneTreeImages();
      const itemsData = await DataService.fetchAllItemsData();
      const fetchedItemImages = await DataService.fetchAllItemImages();
      const summonerSpellImages = await DataService.fetchAllSummonerSpellImages();
      
      setGameWideAssets(prevState => ({
        ...prevState,
        runes: runeImagePaths,
        runeTrees: runeTreeImagePaths,
        runeIcons,
        shards: shardImagePaths,
        itemsDetails: Object.values(itemsData),
        itemImages: fetchedItemImages,
        summonerSpellImages: summonerSpellImages,
        critIcons: {
          physical: gameAssets.critPhysical,
          magic: gameAssets.critMagic,
          true: gameAssets.critTrue,
        },
        ccIcons: crowdControlIcons,
        roleIcons,
        shieldIcon: gameAssets.shield,
        healEmpoweredIcon: gameAssets.healEmpowered,
        energyIcon: gameAssets.energy,
        attackRangeIcon: gameAssets.attackRange,
        abilityHasteIcon: gameAssets.abilityHaste,
        kircheisShardIcon: gameAssets.kircheisShard,
        runeIcon: gameAssets.rune,
        championIcon: gameAssets.champion,
        skillOrderIcon: gameAssets.skillOrder,
        itemIcon: gameAssets.item,
        damageReductionIcon: gameAssets.damageReduction,
        slowResistIcon: gameAssets.slowResist,
        lolCalculatorIcon: gameAssets.lolCalculator,
        trueDamageIcon: gameAssets.trueDamage,
        damageIcon: gameAssets.damageIcon,
        damageIconWhite: gameAssets.damageIconWhite,
        turretIcon: gameAssets.turretIcon,
        turretPlateIcon: gameAssets.turretPlateIcon,
        minionIcon: gameAssets.minionIcon,
        summonersRiftMap: gameAssets.summonersRiftMap,
        }));
    }

    fetchGameWideAssets();
  }, []);

  // Fetch and set initial champion-specific assets
  useEffect(() => {
    if (initialChampionName) {
      fetchAndSetChampionAssets(initialChampionName);
    }
  }, [initialChampionName]);

  const fetchAndSetChampionAssets = useCallback(async (championName: string) => {
    const version = await DataService.fetchLatestVersion();
    const championData = await DataService.fetchChampionHeaderData(championName);
    const championImageUrl = await DataService.getChampionImageUrl(championData.image.full);
  
    const spellImageUrls = {
      Q: `${DataService.baseUrl}/${version}/img/spell/${championData.spells[0].image.full}`,
      W: `${DataService.baseUrl}/${version}/img/spell/${championData.spells[1].image.full}`,
      E: `${DataService.baseUrl}/${version}/img/spell/${championData.spells[2].image.full}`,
      R: `${DataService.baseUrl}/${version}/img/spell/${championData.spells[3].image.full}`,
    };
  
    const customImages = DataService.getCustomSpellImages(championName);
    Object.assign(spellImageUrls, customImages);
  
    const passiveImageUrl = await DataService.getPassiveImageUrl(championData.passive.image.full);
  
    setChampionsAssets(prevState => ({
      ...prevState,
      [championName]: {
        championImageUrl,
        spellImageUrls,
        passiveImageUrl,
        autoAttackImageUrl: gameAssets.autoAttack,
      }
    }));
  }, []);
  
  return (
    <GameAssetsContext.Provider value={{ gameWideAssets, championsAssets, fetchAndSetChampionAssets }}>
      {children}
    </GameAssetsContext.Provider>
  );
};

export default GameAssetsContext;
